import { Timestamp } from 'firebase-admin/firestore'
import { BadgeKind } from '@saasalerts/ui'
import {
  MSPUserRole,
  LicenseScope,
  ScheduledReportBrandingDisplay,
  AlertStatus,
  PartnerBillableStatus,
  ESTimeFormat,
  EventTicket,
  ApplicationType,
} from '@saasalerts/common/shared'
import {
  EventDevice,
  MappedAccount,
  UNIFY_DEVICE_STATUS_DESC,
} from '@saasalerts/common/unify'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'

export type PartnerUser = {
  uid: string
  displayName: string
  email: string
  role: MSPUserRole
  partnerId: string
  isMSPAdmin: boolean
}

export type SelectOption = {
  id: string
  name: string
}

export enum ErrorCode {
  INVALID_TOKEN = 'INVALID_TOKEN',
  PAGE_NOT_FOUND = 'PAGE_NOT_FOUND',
  REQUIRE_AUTHENTICATION = 'REQUIRE_AUTHENTICATION',
  INVALID_ACCESS_KEY = 'INVALID_ACCESS_KEY',
}

export enum ACCOUNT_TYPE {
  MEMBER = 'Member',
  GUEST = 'Guest',
  RESOURCE = 'Resource',
  DELETED = 'Deleted',
}

export enum MFA_REPORT_STATUS {
  MFA_ENABLED = 'MFA Enabled',
  MFA_DISABLED = 'MFA Disabled',
  NO_DATA_PROVIDED = 'No Data Provided',
}

export enum MFA_MS_REPORT_STATUS {
  MFA_ON = 'MFA On',
  MFA_OFF = 'MFA Off',
  NO_DATA_PROVIDED = 'No Data Provided',
}

export enum SSPR_REPORT_STATUS {
  SSPR_ENABLED = 'SSPR Enabled',
  SSPR_DISABLED = 'SSPR Disabled',
  NO_DATA_PROVIDED = 'No Data Provided',
}

export enum MFA_ENFORCED_REPORT_STATUS {
  MFA_ENFORCED = 'MFA Enforced',
  MFA_NOT_ENFORCED = 'MFA Not Enforced',
  NO_DATA_PROVIDED = 'No Data Provided',
}

export enum PASSWORD_STRENGTH_STATUS {
  STRONG = 'Strong',
  WEAK = 'Weak',
  NO_DATA_PROVIDED = 'No Data Provided',
}

export enum REPORTS_ADMIN_STATUS {
  SUPPER_ADMIN = 'Super Admin',
  DELEGATED_ADMIN = 'Delegated Admin',
  BLANK = '',
}

export enum LESS_SECURE_APPS_STATUS {
  TRUE = 'True',
  FALSE = 'False',
  NO_DATA_PROVIDED = 'No Data Provided',
}

export type MFASettingsListItem = {
  name: string
  email: string
  ssprStatus?: SSPR_REPORT_STATUS
  mfaStatus?: MFA_REPORT_STATUS | MFA_MS_REPORT_STATUS
  authMethods: string
  mfaEnforced?: MFA_ENFORCED_REPORT_STATUS
  passwordStrength?: PASSWORD_STRENGTH_STATUS
  lessSecureApps?: LESS_SECURE_APPS_STATUS
  isAdmin?: string
  lastUpdateTimestamp: number | null
}

export enum ACCOUNT_LOGIN_STATUS {
  LOGIN_PERMITTED = 'Login Permitted',
  LOGIN_BLOCKED = 'Login Blocked',
  NOT_APPLICABLE = 'Not Applicable',
}

export interface IAssignedLicense {
  disabledPlans: Array<string>
  skuId: string
}

export interface SecureReportLinkData {
  startDate: string
  endDate: string
  accessKey: string
  expiryDate: string
  shareLink: string
  reportId: string
  organizationId?: string
  date?: string
  apiKey?: string
  applicationType?: string
  accountEmail?: string
  timezone?: string
  branding?: ScheduledReportBrandingDisplay
  fortifySnapshotTemplateId?: string
  benchmarkType?: 'snapshot' | 'template' | 'msftSecureScore'
  snapshotId?: string
}

export type RecommendedActionMap = {
  [eventType: string]: {
    links?: {
      [productType: string]: {
        [linkLabel: string]: string
      }
    }
    recAction: string
  }
}

export type DeviceStatusDescription =
  (typeof UNIFY_DEVICE_STATUS_DESC)[keyof typeof UNIFY_DEVICE_STATUS_DESC]

export type OrganizationAccountEvent = {
  organization: string
  date: string
  account: string
  ip: string
  country: string
  location: string
  description: string
  ticket: string
  additionalDescription: string
  fileName: string
  product: string
  alertSeverity?: string
  asnOwner?: string
  tickets?: EventTicket[]
  eventType: string
  productType: string
  formatted?: {
    tickets?: string
    deviceStatus?: DeviceStatusDescription
    date?: string
  }
  device?: EventDevice | null
}

export type MappedAccounts = {
  account: string
  device: string
  email: string
  id: string
  lastActiveTime: string
  name: string
  operatingSystem: string
  product: string
  mappedAccounts: MappedAccount[]
  mappedDate: string
  mappedBy: string
  formatted?: {
    lastActiveTime: string
    mappedDate: string
    mappedAccounts: string[]
  }
}

export interface Device {
  userEmail: string
  devices: {
    product: {
      name: string
      id: string
      type: string
    }
    usersHistory: [
      {
        usernames: string[]
        time: string
      }
    ]
    deviceName: string
    customer: {
      name: string
      id: string
    }
    _id: string
  }[]
}

export interface MappedDevicesAccounts {
  afterKey: string
  mappedDevicesAccounts: Device[]
}

export interface MappedDevice {
  userEmail: string
  devices: {
    product: {
      name: string
      id: string
      type: string
    }
    usersHistory: [
      {
        usernames: string[]
        time: string
      }
    ]
    customer: {
      name: string
      id: string
    }
    deviceName: string
    lastActiveTime: string
    deviceOS: string
    mappedAccounts: MappedAccount[]
  }[]
}

export interface MappedList {
  id: string
  customer: {
    id: string
    name: string
  }
  product: {
    name: string
    id: string
    type: string
  }
  name: string
  account: string
  device: string
  operatingSystem: string
  lastActiveTime: string
  email: string
  deviceName: string
  deviceOS: string
  mappedBy: string
  mappedDate: string
  mappedAccounts: MappedAccount[]
}

export interface MappedDevices {
  afterKey: string
  mappedDevicesAccounts: MappedDevice[]
}

export type AccountListItem = {
  id: string
  name: string
  email: string
  accountType: ACCOUNT_TYPE
  accountLoginStatus: ACCOUNT_LOGIN_STATUS
  billable: PartnerBillableStatus
  deletedDateTime?: Timestamp
  lastSignIn: Timestamp
}

export type StaleDevices = {
  id: string
  organizationId: string
  productName: string
  productType: string
  deviceName: string
  lastUser: string
  lastActiveTime: string
}

export type UnMappedDevice = {
  deviceName: string
  suggestedAccounts: {
    confidence: number
    userEmail: string
    details: {
      eventUserAgentOSWithDeviceOS: number
      eventUserEmailWithDeviceName: number
      eventIpWithDeviceIp: number
      eventUserEmailWithDeviceUser: number
    }
  }[]
  lastActiveTime: string
  usersHistory: {
    usernames: string[]
    time: ESTimeFormat
  }[]
  deviceOS: string
}

export type IgnoredDevice = {
  deviceName: string
  lastActiveTime: string
  usersHistory: {
    usernames: string[]
    time: ESTimeFormat
  }[]
  deviceOS: string
}

export type UnMappedSuggestedAccountCharts = {
  userEmail: string
  confidence: number
}

export type UnmappedDevices = {
  deviceName: string
  suggestedAccounts: string
  confidenceScore: number
  lastCheckIn: string
  deviceOS: string
  lastLogonUser: {
    usernames: string[]
    time: ESTimeFormat
  }[]
}

export type IgnoredDevices = {
  deviceName: string
  lastCheckIn: string
  deviceOS: string
  lastLogonUser: {
    usernames: string[]
    time: ESTimeFormat
  }[]
}

export type AccountDevice = {
  id: string
  name: string
  email: string
  product?: string
  device?: string
}

export const CHART_TYPES = {
  ORGANIZATION_DETAILS: 'ORGANIZATION_DETAILS',
  TOTAL_ORGANIZATIONS: 'TOTAL_ORGANIZATIONS',
  TOTAL_ACCOUNTS: 'TOTAL_ACCOUNTS',
  BILLABLE_ACCOUNTS: 'BILLABLE_ACCOUNTS',
  APP_WIZARD_DETAILS: 'APP_WIZARD_DETAILS',
} as const

export type ChartType = (typeof CHART_TYPES)[keyof typeof CHART_TYPES]

export type Product = {
  licenseDetails?: string[]
  name: string
  id: string
  type: string
}

export type OrganizationBillingSummary = {
  name: string
  createdAt: string
  accounts: number
  billableAccounts: number
  licenseType?: string
  awApplications?: ApplicationType[]
  products?: Product[]
}

export type ExternallySharedFilesEvent = {
  organization: string
  product: string
  productType: string
  fileName: string
  fileShareExpiration: string
  date: string
  account: string
  sharedWith: Array<string>
  operation?: string
  formatted: { sharedWith?: string; tickets?: string; date?: string }
  tickets?: EventTicket[]
}

export type ApplicationsEvent = {
  organization: string
  date: string
  account: string
  ip: string
  country: string
  location: string
  description: string
  additionalDescription: string
  product: string
  eventType: string
  productType: string
  appName: string
}

export type MessageRules = {
  id: string
  displayName: string
  description: string
  sequence: 1
  isEnabled: boolean
  hasError: boolean
  isReadOnly: boolean
  conditions: {
    sentToMe: boolean
  }
  actions: {
    markImportance: string
    stopProcessingRules: boolean
  }
  deleted?: boolean
}
export type AccountMailboxRules = {
  id: string
  userName: string
  partnerid: string
  organizationId: string
  updatedAt: string
  messageRules: MessageRules
}

export type FileShareEvent = ExternallySharedFilesEvent

export type UnifyEvents = {
  organization: string
  product: string
  date: string
  account: string
  ip: string
  country: string
  location: string
  description: string
  additionalDescription: string
  eventType: string
  productType: string
  appName: string
}

export interface OrganizationsEventsReportParams {
  apiKey: string
  startDate: string
  endDate: string
  organizationId?: string // optional due to backwards compatibility
  alertStatus: AlertStatus
}

export enum AggregatedAlertsOverTimeBasis {
  ACCOUNT = 'user',
  ORGANIZATION = 'customer', // keeping it customer for API compatibility
  THIRD_PARTY_APP = 'appName',
}

export enum ReportFilterField {
  ACCOUNT_EMAIL = 'user-email',
  ORGANIZATION = 'organization',
  DATE_RANGE = 'date-range',
  APPLICATION = 'application',
  DATE = 'date',
  FORTIFY_BENCHMARK_TYPE = 'fortify-benchmark-type',
  FORTIFY_SNAPSHOT = 'fortify-snapshot',
}

export enum Miscellaneous {
  FORTIFY = 'Fortify',
}

export enum REPORT_TYPE {
  ORGANIZATION = 'Executive',
  OPERATIONAL = 'Operational',
  CONFIGURATION = 'Configuration',
}

export type Report = {
  id: string
  name: string
  subTitle?: string
  href: string // path to open the report in the app
  filters: ReportFilterField[]
  printable: boolean // if true, while using a report filter form, it will show print button
  group: string
  reportType: REPORT_TYPE
  domainScope?: LicenseScope[] // if set, report is only visible to these scopes; if absent, visible to all
  tags?: {
    title: string
    color: BadgeKind
    description: string
    icon?: IconDefinition
  }[]
}
